import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SidebarLayout, PageHeader, Card } from '../components/layout';
import { Button } from '../components/ui/button';
import { ExternalOrderModal } from '../components/orders';
import { useAuth } from '../hooks/use-auth';
import { useDivisionContext } from '../contexts/division-context';
import { useDashboardMetrics } from '../hooks/use-orders';
import { getDisplayName } from '../utils';
import { Division } from '../types';

export function HomePage() {
  const { isAuthenticated, signIn } = useAuth();

  // Check if we should redirect to login after logout (for account switching)
  useEffect(() => {
    const shouldRedirectToLogin = sessionStorage.getItem('approvn_redirect_to_login');
    
    if (shouldRedirectToLogin === 'true' && !isAuthenticated) {
      console.log('[HomePage] Detected redirect_to_login flag after logout, initiating login...');
      sessionStorage.removeItem('approvn_redirect_to_login');
      
      // Small delay to ensure logout has fully completed
      setTimeout(() => {
        signIn();
      }, 500);
    }
  }, [isAuthenticated, signIn]);

  return (
    <SidebarLayout>
      <div className="py-4">
        <PageHeader
          title="Welcome to Approv.n"
          subtitle="Single tenant order approval application with organizational divisions"
        />

        {isAuthenticated ? (
          <AuthenticatedContent />
        ) : (
          <PublicContent />
        )}
      </div>
    </SidebarLayout>
  );
}

function PublicContent() {
  return (
    <>
      <div className="max-w-4xl mx-auto mb-8 bg-white dark:bg-secondary-800 rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-4">Register or Update Your Access</h2>
        <p className="text-secondary-600 dark:text-secondary-400 mb-6">
          New to Approv.n? Register for an account to start managing your organization's approval workflows.
          Already registered? Use the same form to add access to another workflow.
        </p>
        <Link to="/register">
          <Button size="lg" className="w-full md:w-auto">
            Register / Update Access
          </Button>
        </Link>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
      <Card>
        <div className="card-header">
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
            Technology Stack
          </h3>
        </div>
        <ul className="space-y-2 text-secondary-600 dark:text-secondary-400">
          <li>• React 18 with TypeScript</li>
          <li>• React Router for navigation</li>
          <li>• Tailwind CSS for styling</li>
          <li>• React Query for state management</li>
          <li>• React Hook Form with Zod</li>
        </ul>
      </Card>

      <Card>
        <div className="card-header">
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
            Authentication
          </h3>
        </div>
        <ul className="space-y-2 text-secondary-600 dark:text-secondary-400">
          <li>• OAuth 2.0/OIDC Integration</li>
          <li>• Keycloak Authentication</li>
          <li>• JWT Token Management</li>
          <li>• Role-based Access Control</li>
          <li>• Division-based Authorization</li>
        </ul>
      </Card>

      <Card>
        <div className="card-header">
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
            Features
          </h3>
        </div>
        <ul className="space-y-2 text-secondary-600 dark:text-secondary-400">
          <li>• Order Management</li>
          <li>• Approval Workflows</li>
          <li>• Division Management</li>
          <li>• User Administration</li>
          <li>• Analytics & Reporting</li>
        </ul>
      </Card>
    </div>
    </>
  );
}

function AuthenticatedContent() {
  const { user, isAdmin, isSupport } = useAuth();
  const displayName = getDisplayName(user?.profile);
  
  // Modal state
  const [isExternalOrderModalOpen, setIsExternalOrderModalOpen] = useState(false);
  
  // Use the global division context
  const {
    selectedDivisionId,
    setSelectedDivisionId,
    divisions,
    shouldShowDivisionSelector,
    currentDivision
  } = useDivisionContext();

  // Get dashboard metrics
  const { 
    data: dashboardData, 
    isLoading: isDashboardLoading, 
    error: dashboardError,
    refetch: refetchDashboard
  } = useDashboardMetrics(selectedDivisionId, !!selectedDivisionId);
  
  const isAdminOrSupport = isAdmin || isSupport;
  
  return (
    <div className="space-y-8">
      {/* Personalized Welcome Message */}
      <Card>
        <div className="text-center py-4">
          <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
            Welcome back, {displayName}!
          </h2>
          <p className="text-secondary-600 dark:text-secondary-400">
            Here's an overview of your approval system activity.
          </p>
          
          {/* Division Selector - Only show if user has access to multiple divisions */}
          {shouldShowDivisionSelector && (
            <div className="mt-4 max-w-xs mx-auto">
              <label htmlFor="division-select" className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-1">
                Division
              </label>
              <select
                id="division-select"
                value={selectedDivisionId}
                onChange={(e) => setSelectedDivisionId(e.target.value)}
                className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
              >
                {divisions.map((division: Division) => (
                  <option key={division.id} value={division.id}>
                    {division.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Show current division info if only one division */}
          {!shouldShowDivisionSelector && currentDivision && (
            <div className="mt-2 text-sm text-secondary-600 dark:text-secondary-400">
              Division: <span className="font-medium">{currentDivision.name}</span>
            </div>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link to="/action-required">
          <Card className="transition-all hover:shadow-md hover:scale-105 cursor-pointer">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900 rounded-md flex items-center justify-center">
                  <div className="w-4 h-4 bg-yellow-600 rounded-sm"></div>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">Action Required</p>
                <p className="text-2xl font-semibold text-secondary-900 dark:text-white" data-testid="metric-action-required">
                  {isDashboardLoading ? (
                    <span className="animate-pulse">--</span>
                  ) : dashboardError ? (
                    <span className="text-red-500">Error</span>
                  ) : (
                    dashboardData?.actionRequiredOrders || 0
                  )}
                </p>
              </div>
            </div>
          </Card>
        </Link>

        <Link to="/completed">
          <Card className="transition-all hover:shadow-md hover:scale-105 cursor-pointer">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-md flex items-center justify-center">
                  <div className="w-4 h-4 bg-green-600 rounded-sm"></div>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">Completed</p>
                <p className="text-2xl font-semibold text-secondary-900 dark:text-white" data-testid="metric-completed">
                  {isDashboardLoading ? (
                    <span className="animate-pulse">--</span>
                  ) : dashboardError ? (
                    <span className="text-red-500">Error</span>
                  ) : (
                    dashboardData?.completedOrders || 0
                  )}
                </p>
              </div>
            </div>
          </Card>
        </Link>
      </div>

      <Card>
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
              Quick Actions
            </h3>
            {dashboardError && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => refetchDashboard()}
                disabled={isDashboardLoading}
              >
                {isDashboardLoading ? 'Refreshing...' : 'Refresh Data'}
              </Button>
            )}
          </div>
        </div>
        
        {dashboardError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
            <p className="text-sm text-red-600 dark:text-red-400">
              Failed to load dashboard data. Please try refreshing.
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-4">
          {isAdminOrSupport && (
            <Button 
              variant="secondary" 
              onClick={() => setIsExternalOrderModalOpen(true)}
            >
              Submit External Order
            </Button>
          )}
          <Link to="/action-required">
            <Button variant="secondary">Action Required</Button>
          </Link>
          {isAdmin && (
            <Link to="/configuration">
              <Button variant="outline">Configuration</Button>
            </Link>
          )}
        </div>
      </Card>

      {/* External Order Modal */}
      <ExternalOrderModal
        isOpen={isExternalOrderModalOpen}
        onClose={() => setIsExternalOrderModalOpen(false)}
      />
    </div>
  );
}
